import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  FunnelIcon,
  ClockIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import {
  useAuditoria,
  useAuditoriaTipos,
  useAuditoriaUsuariosDistintos,
  useAuditoriaEntidades,
  useAuditoriaNumeros,
} from '../../hooks/useAuditoria';
import { useDebounced } from '../../hooks/useDebounced';
import { useToast } from '../ui/Toast';
import { inputIris } from '../ui/iris';
import { DialogoEvento } from './DialogoEvento';
import { corPorPrefixo } from '../../utils/corAuditoria';
import { fmtNumero } from '../../utils/formatters';
import { baixarBlob } from '../../utils/baixarBlob';
import { periodoServeParaBusca, ultimos90DiasIso, toIsoDateLocal } from '../../utils/periodoBusca';
import { auditoriaApi, AUDITORIA_MAX_EXPORT } from '../../api/auditoria';
import type { AuditoriaItem, AuditoriaFiltros } from '../../types/auditoria';

const fmtData = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return '—';
  }
};

// Default ao abrir: últimas 24h. UI usa <input type="date"> que entrega
// "YYYY-MM-DD" — no backend isso é parseado como meia-noite local. Formata
// via toIsoDateLocal (calendário local, não UTC) — `toISOString()` aqui
// devolvia "amanhã" depois das ~21h em UTC-3 (ver periodoBusca.ts).
function isoOntem(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toIsoDateLocal(d);
}

const PAGE_SIZE = 50;

/** Aba "Eventos" — grid filtrável da auditoria (corpo original da AuditoriaPage). */
export const AbaEventos: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [tipo, setTipo] = useState<string>('');
  const [usuario, setUsuario] = useState<string>('');
  const [periodoInicio, setPeriodoInicio] = useState<string>(isoOntem());
  const [periodoFim, setPeriodoFim] = useState<string>('');
  const [entidade, setEntidade] = useState<string>('');
  const [entidadeId, setEntidadeId] = useState<string>('');
  const [correlationId, setCorrelationId] = useState<string>('');
  const [q, setQ] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [itemSelecionado, setItemSelecionado] = useState<AuditoriaItem | null>(null);
  const [showFiltrosExtra, setShowFiltrosExtra] = useState(false);
  const [exportando, setExportando] = useState(false);

  // Debounce só atrasa o `q` que VAI PRO FILTRO (evita 1 request por tecla).
  const debouncedQ = useDebounced(q, 300);

  // Busca textual sem período (≤90d) completo 400a no backend — aqui vira
  // "não dispare a query" + dica inline, nunca um 400 cru na tela.
  //
  // Dica inline (banner) usa o `q` CRU — precisa reagir na hora que o usuário
  // digita, senão pisca 300ms atrasada.
  const periodoValidoCru = periodoServeParaBusca(q, periodoInicio, periodoFim);
  // `enabled`/Exportar precisam valer pros DOIS (`q` cru E `debouncedQ`): o
  // request de verdade sai com `filtros.q` = `debouncedQ`. Gatear só no cru
  // deixa a query destravar na hora que o usuário limpa o campo (`q`→''), mas
  // `debouncedQ` ainda carrega o termo antigo por até 300ms — request sem
  // período válido pro backend = 400. Achado real: digitar com período
  // inválido, clicar Limpar, ver o 400 piscar antes do debounce assentar.
  const periodoValidoParaBusca = periodoValidoCru && periodoServeParaBusca(debouncedQ, periodoInicio, periodoFim);

  const filtros: AuditoriaFiltros = useMemo(() => ({
    tipo: tipo || undefined,
    usuario: usuario || undefined,
    periodoInicio: periodoInicio || undefined,
    periodoFim: periodoFim || undefined,
    entidade: entidade || undefined,
    entidadeId: entidadeId || undefined,
    correlationId: correlationId || undefined,
    q: debouncedQ.trim() || undefined,
    page,
    pageSize: PAGE_SIZE,
  }), [tipo, usuario, periodoInicio, periodoFim, entidade, entidadeId, correlationId, debouncedQ, page]);

  const { data, isLoading, isFetching, error, refetch } = useAuditoria(filtros, { enabled: periodoValidoParaBusca });
  const { data: tipos = [] } = useAuditoriaTipos();
  const { data: usuarios = [] } = useAuditoriaUsuariosDistintos();
  const { data: entidades = [] } = useAuditoriaEntidades();

  // KPIs do topo: uma query só (`numeros(1)`) alimenta os dois — é o mesmo
  // agregado que a aba Números usa, em vez do hack antigo de pageSize=1
  // disparando uma request extra por card. dias=1 no backend é
  // `DateTime.Today.AddDays(-1+1)` = HOJE desde a meia-noite local, não uma
  // janela deslizante de 24h — por isso os rótulos dizem "hoje", nunca "24h".
  const { data: numerosHoje } = useAuditoriaNumeros(1);
  const eventosHoje = numerosHoje?.total;
  // "Logins hoje" = contagem exata do backend (`loginsSucesso`), não varredura
  // de `porTipo` — esse vem Take(15) e "auth.login" pode ficar de fora do top
  // 15 num dia com muitos tipos distintos, mostrando 0 falso.
  const loginsHoje = numerosHoje?.loginsSucesso;

  // Deep-link pra Timeline — "o que mais aconteceu com ESTA entidade". Só
  // nasce quando a linha tem os dois campos (o endpoint exige ambos, 400 sem).
  const verTimeline = (item: AuditoriaItem) => {
    if (!item.entidade || !item.entidadeId) return;
    const params = new URLSearchParams({ aba: 'timeline', entidade: item.entidade, id: item.entidadeId });
    navigate(`/auditoria?${params.toString()}`);
  };

  const periodoInvalido = periodoFim !== '' && periodoInicio !== '' && periodoFim < periodoInicio;

  const filtrosExtraAtivos = !!(usuario || entidade || entidadeId || correlationId);

  const limparFiltros = () => {
    setTipo('');
    setUsuario('');
    setPeriodoInicio(isoOntem());
    setPeriodoFim('');
    setEntidade('');
    setEntidadeId('');
    setCorrelationId('');
    setQ('');
    setPage(1);
  };

  // CTA da dica de busca inválida — ajusta as datas VISIVELMENTE (o usuário
  // vê os inputs mudarem) em vez de tentar adivinhar/mandar a request mesmo
  // assim. Nunca silencioso: é o próprio usuário quem vê e decide manter.
  const ajustarPeriodoParaBusca = () => {
    const { inicio, fim } = ultimos90DiasIso();
    setPeriodoInicio(inicio);
    setPeriodoFim(fim);
    setPage(1);
  };

  // Exporta com os filtros ATUAIS da tela (mesmo objeto que alimenta a listagem).
  const exportar = async () => {
    setExportando(true);
    try {
      const { blob, filename } = await auditoriaApi.exportar(filtros);
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      baixarBlob(filename ?? `utilyhub-auditoria-${stamp}.json`, blob);
    } catch {
      toast.erro('Não foi possível exportar os eventos.');
      return;
    } finally {
      setExportando(false);
    }

    // Detecção do truncamento fica FORA do try/catch do download, num bloco
    // próprio: o arquivo já foi salvo com sucesso nesse ponto, então qualquer
    // exceção aqui (improvável, mas não custa isolar) não pode virar
    // "Não foi possível exportar" depois que exportou de verdade.
    //
    // `totalCount` vem da MESMA listagem que já roda com `filtros` (page/pageSize
    // não afetam a contagem) — sinal exato e grátis, sem reabrir/parsear o blob
    // baixado. Comparação ESTRITA (`>`, não `>=`): total == teto é o caso em que
    // NADA ficou de fora, `>=` avisaria "refine o filtro" sem restante nenhum.
    try {
      const total = data?.totalCount ?? 0;
      if (total > AUDITORIA_MAX_EXPORT) {
        toast.aviso(
          `Exportação truncada em ${AUDITORIA_MAX_EXPORT.toLocaleString('pt-BR')} de ${total.toLocaleString('pt-BR')} eventos — refine o filtro (período, tipo, usuário) pra baixar o restante.`,
          8000,
        );
      }
    } catch {
      // best-effort — aviso de truncamento não pode encobrir o export já concluído.
    }
  };

  const totalPages = data?.totalPages ?? 1;
  const totalCount = data?.totalCount ?? 0;
  const inicio = data ? (data.currentPage - 1) * data.pageSize + 1 : 0;
  const fim = data ? Math.min(data.currentPage * data.pageSize, data.totalCount) : 0;

  return (
    // Raiz com h-full flex-col preserva a cadeia de altura que faz a tabela
    // rolar internamente (ver AuditoriaPage.tsx — shell aplica o mesmo h-full).
    <div className="flex h-full flex-col gap-3">
      {/* KPIs — Eventos/Logins de HOJE (numeros(1).total), uma query só. */}
      <div className="grid grid-cols-2 gap-3 sm:w-80">
        <KpiCard label="Eventos hoje" valor={eventosHoje} />
        <KpiCard label="Logins hoje" valor={loginsHoje} />
      </div>

      {/* Toolbar de filtros */}
      <div className="rounded-iris-lg border border-line bg-surface p-2.5">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col">
            <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">Tipo</label>
            <select
              value={tipo}
              onChange={(e) => { setTipo(e.target.value); setPage(1); }}
              className={`min-w-[180px] ${inputIris} h-8 text-xs`}
            >
              <option value="">Todos os tipos</option>
              {tipos.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">De</label>
            <input
              type="date"
              value={periodoInicio}
              onChange={(e) => { setPeriodoInicio(e.target.value); setPage(1); }}
              className={`${inputIris} h-8 text-xs`}
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">Até</label>
            <input
              type="date"
              value={periodoFim}
              onChange={(e) => { setPeriodoFim(e.target.value); setPage(1); }}
              className={`${inputIris} h-8 text-xs ${periodoInvalido ? '!border-danger' : ''}`}
            />
          </div>

          <div className="flex min-w-[200px] flex-1 flex-col">
            <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">Busca</label>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-tx-3" />
              <input
                type="text"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Busca no conteúdo do evento (exige período ≤ 90 dias)"
                className={`w-full ${inputIris} h-8 pl-7 text-xs`}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowFiltrosExtra(v => !v)}
            className={`flex h-8 items-center gap-1.5 rounded-iris px-2.5 text-xs font-medium transition-colors ${
              showFiltrosExtra || filtrosExtraAtivos
                ? 'bg-accent-soft text-accent-text'
                : 'border border-line text-tx-2 hover:bg-surface-2'
            }`}
          >
            <FunnelIcon className="h-3.5 w-3.5" />
            Filtros
          </button>

          <button
            type="button"
            onClick={limparFiltros}
            className="h-8 rounded-iris border border-line px-2.5 text-xs text-tx-2 hover:bg-surface-2 transition-colors"
          >
            Limpar
          </button>

          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex h-8 items-center gap-1 rounded-iris border border-line px-2.5 text-xs text-tx-2 hover:bg-surface-2 transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </button>

          <button
            type="button"
            onClick={exportar}
            disabled={exportando || !periodoValidoParaBusca}
            title={
              !periodoValidoParaBusca
                ? 'Ajuste o período (busca textual exige De + Até de até 90 dias) pra exportar'
                : 'Baixar os eventos deste filtro em JSON'
            }
            className="flex h-8 items-center gap-1 rounded-iris border border-line px-2.5 text-xs text-tx-2 hover:bg-surface-2 transition-colors disabled:opacity-50"
          >
            <ArrowDownTrayIcon className={`h-3.5 w-3.5 ${exportando ? 'animate-pulse' : ''}`} />
            {exportando ? 'Exportando…' : 'Exportar'}
          </button>
        </div>

        {showFiltrosExtra && (
          <div className="mt-2.5 pt-2.5 border-t border-line flex flex-wrap items-end gap-2">
            <div className="flex flex-col">
              <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">Usuário</label>
              <select
                value={usuario}
                onChange={(e) => { setUsuario(e.target.value); setPage(1); }}
                className={`min-w-[140px] ${inputIris} h-8 text-xs`}
              >
                <option value="">Todos os usuários</option>
                {usuarios.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">Entidade</label>
              <select
                value={entidade}
                onChange={(e) => { setEntidade(e.target.value); setPage(1); }}
                className={`min-w-[160px] ${inputIris} h-8 text-xs`}
              >
                <option value="">Todas as entidades</option>
                {entidades.map((e) => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">ID da entidade</label>
              <input
                type="text"
                value={entidadeId}
                onChange={(e) => { setEntidadeId(e.target.value); setPage(1); }}
                placeholder="ex: 01|12345"
                className={`w-32 ${inputIris} h-8 text-xs`}
              />
            </div>

            <div className="flex flex-col">
              <label className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-tx-3">Correlation ID</label>
              <input
                type="text"
                value={correlationId}
                onChange={(e) => { setCorrelationId(e.target.value); setPage(1); }}
                placeholder="32 hex"
                className={`w-44 font-mono ${inputIris} h-8 text-[11px]`}
              />
            </div>
          </div>
        )}
      </div>

      {periodoInvalido && (
        <div className="rounded-iris border border-danger-border bg-danger-soft px-3 py-1.5 text-xs text-danger">
          Período inválido: data final é anterior à inicial.
        </div>
      )}

      {/* Busca textual sem período (De+Até, ≤90d) NÃO dispara a query — o
          backend devolveria 400. Ação de 1 clique ajusta as datas visivelmente
          (o usuário vê o "De"/"Até" mudar) em vez de silenciar o problema. */}
      {q.trim() !== '' && !periodoValidoCru && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-iris border border-warning-border bg-warning-soft px-3 py-1.5 text-xs text-warning">
          <span>Busca textual exige período completo (De + Até) de até 90 dias.</span>
          <button
            type="button"
            onClick={ajustarPeriodoParaBusca}
            className="rounded-iris-sm border border-warning-border px-2 py-1 text-[11px] font-medium text-warning hover:opacity-80 transition-opacity"
          >
            Usar últimos 90 dias
          </button>
        </div>
      )}

      {/* Tabela */}
      <div className="flex-1 overflow-auto rounded-iris-lg border border-line bg-surface">
        {!periodoValidoParaBusca ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-tx-3">
            <MagnifyingGlassIcon className="h-10 w-10 text-tx-3 opacity-50" />
            <span>Ajuste o período pra ver os resultados da busca.</span>
          </div>
        ) : isLoading ? (
          <p className="p-6 text-center text-sm text-tx-3">Carregando eventos...</p>
        ) : error ? (
          <p className="p-6 text-center text-sm text-danger">Erro ao carregar auditoria.</p>
        ) : !data || data.data.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-tx-3">
            <ShieldCheckIcon className="h-10 w-10 text-tx-3 opacity-50" />
            <span>Nenhum evento de auditoria encontrado neste filtro.</span>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-surface-2">
              <tr className="border-b border-line text-left text-[11px] font-semibold uppercase tracking-wide text-tx-3">
                <th className="py-2 px-2">Quando</th>
                <th className="py-2 px-2">Tipo</th>
                <th className="py-2 px-2">Usuário</th>
                <th className="py-2 px-2">Entidade</th>
                <th className="py-2 px-2">ID</th>
                <th className="py-2 px-2">Correlation</th>
                <th className="py-2 px-2">IP</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-line last:border-0 hover:bg-surface-2 transition-colors"
                >
                  <td className="whitespace-nowrap py-1.5 px-2 font-mono text-[11px] text-tx-2">
                    {fmtData(item.criadoEm)}
                  </td>
                  <td className="py-1.5 px-2">
                    <span className={`inline-block rounded-iris-sm px-2 py-0.5 text-[10px] font-semibold ${corPorPrefixo(item.tipo)}`}>
                      {item.tipo}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 font-medium text-tx">
                    {item.usuario || <span className="text-tx-3">—</span>}
                  </td>
                  <td className="py-1.5 px-2 text-tx-2">
                    {item.entidade || <span className="text-tx-3">—</span>}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[11px] text-tx-2">
                    {item.entidadeId || <span className="text-tx-3">—</span>}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-tx-3" title={item.correlationId ?? undefined}>
                    {item.correlationId ? item.correlationId.slice(0, 8) + '…' : '—'}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-[10px] text-tx-3">
                    {item.ip || '—'}
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {item.entidade && item.entidadeId && (
                        <button
                          type="button"
                          onClick={() => verTimeline(item)}
                          title="Ver tudo desta entidade (aba Timeline)"
                          className="inline-flex items-center gap-1 rounded-iris-sm border border-line px-2 py-0.5 text-[11px] text-tx-2 hover:bg-surface-2 transition-colors"
                        >
                          <ClockIcon className="h-3 w-3" /> Timeline
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setItemSelecionado(item)}
                        className="inline-flex items-center gap-1 rounded-iris-sm border border-line px-2 py-0.5 text-[11px] text-tx-2 hover:bg-surface-2 transition-colors"
                      >
                        <EyeIcon className="h-3 w-3" /> Ver JSON
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação — some junto com a tabela quando a busca está pausada
          (senão mostraria a contagem da última página válida, incoerente
          com o placeholder "ajuste o período" acima). */}
      {periodoValidoParaBusca && data && data.totalCount > 0 && (
        <div className="flex items-center justify-between gap-2 rounded-iris-lg border border-line bg-surface px-3 py-2 text-xs">
          <span className="text-tx-2">
            Mostrando {inicio}–{fim} de {totalCount.toLocaleString('pt-BR')} eventos
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1}
              className="rounded-iris-sm border border-line px-2 py-1 text-xs text-tx-2 hover:bg-surface-2 disabled:opacity-40"
            >
              Primeira
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-iris-sm border border-line px-2 py-1 text-xs text-tx-2 hover:bg-surface-2 disabled:opacity-40"
            >
              Anterior
            </button>
            <span className="px-2 text-tx-2">
              Página {page} de {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-iris-sm border border-line px-2 py-1 text-xs text-tx-2 hover:bg-surface-2 disabled:opacity-40"
            >
              Próxima
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              className="rounded-iris-sm border border-line px-2 py-1 text-xs text-tx-2 hover:bg-surface-2 disabled:opacity-40"
            >
              Última
            </button>
          </div>
        </div>
      )}

      {/* Modal JSON */}
      {itemSelecionado && (
        <DialogoEvento item={itemSelecionado} onFechar={() => setItemSelecionado(null)} />
      )}
    </div>
  );
};

const KpiCard: React.FC<{ label: string; valor?: number }> = ({ label, valor }) => (
  <div className="rounded-iris-lg border border-line bg-surface p-4">
    <div className="text-2xl font-bold leading-tight text-tx">{fmtNumero(valor)}</div>
    <div className="mt-0.5 text-xs font-medium text-tx-2">{label}</div>
  </div>
);

export default AbaEventos;
